import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ParseKeys } from 'i18next';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { useCheckHeader } from 'Src/feature/check/useCheckHeader';
import { usePrintRetry } from 'Src/feature/check-print/usePrintRetry';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useKDSDisplay } from 'Src/feature/kdsDisplay/useKDSDisplay';
import { usePrinter } from 'Src/feature/printer/usePrinter';

import { CheckPrintJobFailure } from './types';
import { NotificationUI } from './useNotification';

import styles from './PrintFailureToastNotification.module.scss';

const sectionId = 'PrintFailureToastNotification';

interface PrintFailureToastNotificationProps {
  notification: NotificationUI<CheckPrintJobFailure>;
  onAction?: () => void;
}

const PrintFailureToastNotification: FC<PrintFailureToastNotificationProps> = ({
  notification,
  onAction,
}) => {
  const { t } = useTranslation();
  const { data } = notification;

  const log = useLogger().getSubLogger({ name: sectionId });
  const printRetry = usePrintRetry.mutate.retry();
  const printerMap = usePrinter.query.map();
  const kdsDisplay = useKDSDisplay.query.getOne(data.device?.deviceType === 'display' ? data.device?.deviceId : undefined);
  const checkHeader = useCheckHeader.query.collection();

  let i18nKey: ParseKeys<'translation'> = 'notification.printFailure.unhandledFailedAt';
  let deviceName: string | undefined = t('notification.printFailure.unknownDevice');
  switch (data.device?.deviceType) {
    case 'printer':
      i18nKey = 'notification.printFailure.printingFailedAt';
      deviceName = printerMap.data?.get(data.device?.deviceId)?.name;
      break;
    case 'display':
      i18nKey = 'notification.printFailure.displayFailedAt';
      deviceName = kdsDisplay.data?.name;
      break;
  }

  const checkHeaderFromNotification = checkHeader.data ? checkHeader.data.map.get(notification.data.checkId) : undefined;
  const isGiftCardChit = data.type === 'GiftCardChit';


  return (
    <Flex
      horizontal
      alignItems="center"
      justifyContent="space-between"
      className={styles.root}
    >
      <Flex gap="sm">
        <Copy
          variant="body"
          weight="md"
          value={checkHeaderFromNotification?.label ? t('notification.printFailure.groupTitleCheck', { checkLabel: checkHeaderFromNotification?.label }) : t('notification.printFailure.groupTitle')}
        />
        {isGiftCardChit && data.device?.deviceType === 'printer' ? (
          <Copy
            variant="body"
            size="sm"
            weight="regular"
            value={t('notification.printFailure.giftcardFailedAt', { deviceName: deviceName || t('notification.printFailure.unknownDevice') })}
          />
        ) : (
          <Copy
            variant="body"
            size="sm"
            weight="regular"
            value={t(i18nKey, { checkLabel: checkHeader.data?.map.get(data.checkId)?.label ?? t('notification.printFailure.unknownCheckNumber'), deviceName, errStr: data.errStr })}
          />
        )}
      </Flex>
      {data.device?.deviceType !== 'display' && !isGiftCardChit && (
        <CTA
          color="textPrimary"
          label={t('notification.printFailure.retry')}
          onClick={() => {
            printRetry.mutate({ checkId: data.checkId, printJobId: data.id }, {
              onSettled: () => {
                if (onAction) onAction();
              },
              onError: (err) => log.error('printRetry.onClick', err)
            });
          }}
        />
      )}
    </Flex>
  );
};

export type { PrintFailureToastNotificationProps };
export { PrintFailureToastNotification, sectionId };
